import { a as TenantService, T as TenantApi } from './tenant-service-DlhdZAs9.js';
import { g as globalCacheService, C as CacheTTL } from './index--OssIds0.js';

/**
 * Tenant Manager - Clean, high-level interface for tenant operations
 *
 * Provides a simplified API for common tenant management tasks while maintaining
 * access to the full tenant SDK for advanced use cases.
 */
class TenantManager {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.cache = globalCacheService.createNamespace('tenant');
        const tenantApi = new TenantApi(apiClient);
        this.tenantService = new TenantService(tenantApi);
    }
    /**
     * Get current tenant public information (API key only)
     *
     * @returns Promise resolving to public tenant data
     */
    async getTenantPublicInfo() {
        return this.tenantService.getRemoteTenantPublic();
    }
    /**
     * Get full tenant information including sensitive config (Admin JWT required)
     *
     * Returns additional fields like allowedTokenIssuers that are only
     * available to authenticated tenant admins.
     *
     * @returns Promise resolving to full tenant data
     */
    async getTenantInfo() {
        return this.tenantService.getRemoteTenant();
    }
    /**
     * Get tenant login token
     *
     * @returns Promise resolving to login token
     */
    async getLoginToken() {
        return this.tenantService.getRemoteLoginToken();
    }
    /**
     * Get tenant client configuration (cached)
     *
     * @returns Promise resolving to client config
     */
    async getClientConfig() {
        return this.cache.getOrSet('clientConfig', () => this.tenantService.getRemoteClientConfig(), CacheTTL.LONG);
    }
    /**
     * Get Google API key from tenant configuration (cached).
     *
     * @returns Google API key or undefined if not configured
     */
    async getGoogleApiKey() {
        const config = await this.getClientConfig();
        return config.googleApiKey;
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * Admin: Get current admin info
     *
     * Retrieves the authenticated admin's profile information.
     * Requires tenant admin authentication.
     *
     * @returns Promise resolving to current admin data
     * @throws {PersApiError} When not authenticated as tenant admin
     *
     * @example
     * ```typescript
     * const admin = await sdk.tenants.getCurrentAdmin();
     * console.log('Current admin:', admin.email);
     * ```
     */
    async getCurrentAdmin() {
        return this.tenantService.getCurrentAdmin();
    }
    /**
     * Admin: Update tenant data
     *
     * @param tenantData - Updated tenant data
     * @returns Promise resolving to full updated tenant (includes sensitive config)
     */
    async updateTenant(tenantData) {
        return this.tenantService.updateRemoteTenant(tenantData);
    }
    /**
     * Admin: Get all admins
     *
     * @param options - Pagination options
     * @returns Promise resolving to paginated admins
     */
    async getAdmins(options) {
        return this.tenantService.getAdmins(options);
    }
    /**
     * Admin: Create new admin
     *
     * @param adminData - Admin data
     * @returns Promise resolving to created admin
     */
    async createAdmin(adminData) {
        return this.tenantService.postAdmin(adminData);
    }
    /**
     * Admin: Update existing admin
     *
     * @param adminId - ID of the admin to update
     * @param adminData - Updated admin data
     * @returns Promise resolving to updated admin
     */
    async updateAdmin(adminId, adminData) {
        return this.tenantService.putAdmin(adminId, adminData);
    }
    /**
     * Get the tenant service for advanced operations
     *
     * @returns TenantService instance
     */
    getTenantService() {
        return this.tenantService;
    }
    /**
     * Clear tenant cache (useful after config changes)
     */
    clearCache() {
        this.cache.clear();
    }
    // ==========================================
    // IPFS OPERATIONS (Lightweight, no Web3 deps)
    // ==========================================
    /**
     * Get the IPFS gateway domain from tenant config (cached)
     *
     * @returns Promise resolving to IPFS gateway domain (e.g., 'gateway.pinata.cloud')
     * @example
     * ```typescript
     * const gateway = await sdk.tenants.getIpfsGatewayDomain();
     * // Returns 'gateway.pinata.cloud' or tenant-specific gateway
     * ```
     */
    async getIpfsGatewayDomain() {
        return this.cache.getOrSet('ipfsGateway', async () => {
            const config = await this.getClientConfig();
            return config.ipfsGatewayDomain || 'gateway.pinata.cloud';
        }, CacheTTL.LONG);
    }
    /**
     * Resolve an IPFS URL to an HTTP gateway URL
     *
     * @param url - The URL to resolve (ipfs://, ar://, or HTTP URL)
     * @returns Promise resolving to HTTP gateway URL
     *
     * @example
     * ```typescript
     * const httpUrl = await sdk.tenants.resolveIpfsUrl('ipfs://QmXxx...');
     * // Returns 'https://gateway.pinata.cloud/ipfs/QmXxx...'
     * ```
     */
    async resolveIpfsUrl(url) {
        if (!url)
            return '';
        // Already an HTTP URL
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        const gateway = await this.getIpfsGatewayDomain();
        // Handle ipfs:// protocol
        if (url.startsWith('ipfs://')) {
            const cid = url.replace('ipfs://', '');
            return `https://${gateway}/ipfs/${cid}`;
        }
        // Handle ar:// (Arweave) protocol
        if (url.startsWith('ar://')) {
            const txId = url.replace('ar://', '');
            return `https://arweave.net/${txId}`;
        }
        return url;
    }
    /**
     * Fetch and process token metadata from IPFS/HTTP URI
     *
     * @param tokenUri - The token URI (ipfs://, ar://, or HTTP)
     * @returns Promise resolving to processed metadata or null on error
     *
     * @example
     * ```typescript
     * const metadata = await sdk.tenants.fetchAndProcessMetadata('ipfs://QmXxx.../metadata.json');
     * if (metadata) {
     *   console.log(metadata.name, metadata.imageUrl);
     * }
     * ```
     */
    async fetchAndProcessMetadata(tokenUri) {
        try {
            const resolvedUri = await this.resolveIpfsUrl(tokenUri);
            const response = await fetch(resolvedUri);
            if (!response.ok) {
                console.warn(`Failed to fetch metadata from ${resolvedUri}: ${response.status}`);
                return null;
            }
            const rawMetadata = await response.json();
            // Normalize the metadata - resolve image URLs
            const imageUrl = rawMetadata.image || rawMetadata.image_url || rawMetadata.imageUrl || '';
            const resolvedImageUrl = await this.resolveIpfsUrl(imageUrl);
            const animationUrl = rawMetadata.animation_url || rawMetadata.animationUrl || '';
            const resolvedAnimationUrl = animationUrl ? await this.resolveIpfsUrl(animationUrl) : undefined;
            return {
                name: rawMetadata.name || 'Untitled',
                description: rawMetadata.description || '',
                imageUrl: resolvedImageUrl,
                animationUrl: resolvedAnimationUrl,
                externalUrl: rawMetadata.external_url || rawMetadata.externalUrl,
                attributes: rawMetadata.attributes,
                ...rawMetadata // Include any additional properties
            };
        }
        catch (error) {
            console.warn(`Error fetching metadata from ${tokenUri}:`, error);
            return null;
        }
    }
}

export { TenantManager as T };
//# sourceMappingURL=tenant-manager-xmYKBFGu.js.map
